/* eslint-disable react-hooks/exhaustive-deps */
// React
import { ChangeEvent, useEffect, useState } from "react"

// Components
import {
  Button,
  Card,
  DateInput,
  FooterMenu,
  HeaderMenu,
  Input,
  Label,
  Loading,
  Modal,
  Select,
  Textarea
} from "components"

// Form
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  useWatch
} from "react-hook-form"

// Third-Party Libraries
import moment from "moment"
import toast from "react-hot-toast"

// Types
import type { SelectOptionType } from "types"
import type { DataType } from "./types"
import { exportExcel } from "./utils/functions"

// Utils
import { useApi, useHandleAuth, useToggle } from "utils"
import {
  useCountry,
  useProvince,
  useRegency,
  useSubdistrict,
  useVillage
} from "../Project/components/FormSection/utils"
import { useExportPdfCompany, useList } from "./utils"

export default function SettingCompany() {
  // Hooks
  const api = useApi()
  const auth = useHandleAuth()
  const token = auth?.userTokenData()
  const { isError, isLoading, fetchData } = useList()

  // Form
  const defaultValues: DataType = {
    company_code: token?.company_code ?? "",
    company_name: token?.company_name ?? "-",
    subdomain: token?.subdomain ?? "-",
    address: "",
    barcode_type: "",
    country_id: "",
    email: "",
    finance_year: "",
    methode_avg_price: "",
    mobile_phone: "",
    phone: "",
    province_id: "",
    regency_id: "",
    subdistrict_id: "",
    village_id: "",
    website: "",
    period_adjustment: "",
    tax: 20,
    logo_path: null
  }

  const methods = useForm<DataType>({ defaultValues })
  const exportPdf = useExportPdfCompany({ dataInit: methods.getValues() })

  useEffect(() => {
    fetchData().then((data) => {
      methods.reset({
        ...data,
        tax: data?.tax ?? 20,
        logo_path: data?.logo_path
      });
    });
  }, []);

  const onSubmit = (value: DataType) => {
    return new Promise<void>((resolve) => {
      toast
        .promise(
          api.post(`/settingcompany/`, {
            ...value,
            country_id: value.country_id !== "" ? value.country_id : undefined,
            finance_year:
              value.finance_year !== "" ? value.finance_year : undefined,
            province_id:
              value.province_id !== "" ? value.province_id : undefined,
            regency_id: value.regency_id !== "" ? value.regency_id : undefined,
            subdistrict_id:
              value.subdistrict_id !== "" ? value.subdistrict_id : undefined,
            village_id: value.village_id !== "" ? value.village_id : undefined
          }),
          {
            loading: "Loading...",
            success: (res) => res.data.message,
            error: (err) => err.response.data.message ?? err.response.message
          }
        )
        .then(() => { })
        .catch(() => { })
        .finally(resolve)
    })
  }

  return (
    <section className="container pt-5 gap-5 flex flex-col">
      <HeaderMenu title="COMPANY SETUP" />

      <Card>
        <Card.Body>
          {Boolean(isError || isLoading) ? (
            <Loading errorText="No data available" loading={isLoading} />
          ) : (
            <FormProvider {...methods}>
              <section className="w-full flex lg:flex-row flex-col gap-3 mb-4">


                <section className="w-1/2 flex flex-col gap-3">
                  <Controller
                    control={methods.control}
                    name="company_code"
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={field.value?.toUpperCase() ?? ""}
                        disabled
                        type="text"
                        label="COMPANY CODE"
                      />
                    )}
                  />

                  <Controller
                    control={methods.control}
                    name="company_name"
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={field.value?.toUpperCase() ?? ""}
                        disabled
                        type="text"
                        label="COMPANY NAME"
                      />
                    )}
                  />

                  <Controller
                    control={methods.control}
                    name="subdomain"
                    render={({ field }) => (
                      <Input {...field} disabled type="text" label="SUBDOMAIN" />
                    )}
                  />

                  <Controller
                    control={methods.control}
                    name="email"
                    render={({ field }) => (
                      <Input {...field} type="email" label="EMAIL" />
                    )}
                  />

                  <Controller
                    control={methods.control}
                    name="phone"
                    render={({ field }) => <Input {...field} label="PHONE" />}
                  />

                  <Controller
                    control={methods.control}
                    name="mobile_phone"
                    render={({ field }) => <Input {...field} label="MOBILE" />}
                  />

                  <Controller
                    control={methods.control}
                    name="website"
                    render={({ field }) => <Input {...field} label="WEBSITE" />}
                  />

                  <Controller
                    control={methods.control}
                    name="finance_year"
                    render={({ field, fieldState }) => (
                      <DateInput
                        label="FINANCE YEAR"
                        error={fieldState.error?.message}
                        ref={field.ref}
                        onChange={(value) => {
                          if (value) {
                            field.onChange(moment(value).format("YYYY"))
                          } else {
                            field.onChange("")
                          }
                        }}
                        selected={
                          field.value
                            ? moment(field.value, "YYYY").toDate()
                            : null
                        }
                        dateFormat="yyyy"
                        showYearPicker
                      />
                    )}
                  />

                  <Controller
                    control={methods.control}
                    name="methode_avg_price"
                    render={({ field, fieldState }) => (
                      <Select
                        label="METODE AVERAGE PRICE"
                        placeholder="Select Metode"
                        error={fieldState.error?.message}
                        options={dropdownAverage}
                        value={dropdownAverage.find(
                          (item) => item.value === field.value
                        )}
                        onChange={(e) => {
                          if (e) {
                            field.onChange(e.value)
                          }
                        }}
                      />
                    )}
                  />

                  <Controller
                    control={methods.control}
                    name="tax"
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={field?.value ? field.value?.toString() : "0"}
                        label="TAX"
                        onKeyDown={(e) => {
                          const allowedKeys = [
                            "Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete", ".", ","
                          ];

                          if (
                            !/[0-9]/.test(e.key) &&
                            !allowedKeys.includes(e.key)
                          ) {
                            e.preventDefault();
                          }
                        }}
                      />
                    )}
                  />
                </section>

                <section className="w-1/2 flex flex-col gap-3">

                  <Controller
                    control={methods.control}
                    name="period_adjustment"
                    render={({ field, fieldState }) => (
                      <Select
                        label="PERIODE ADJUSTMENT"
                        placeholder="Select Periode"
                        error={fieldState.error?.message}
                        options={periodeAdjustment}
                        value={periodeAdjustment.find(
                          (item) => item.value === field.value
                        )}
                        onChange={(e) => {
                          if (e) {
                            field.onChange(e.value)
                          }
                        }}
                      />
                    )}
                  />

                  <Controller
                    control={methods.control}
                    name="barcode_type"
                    render={({ field, fieldState }) => (
                      <Select
                        label="TYPE BARCODE"
                        placeholder="Select Type"
                        error={fieldState.error?.message}
                        options={itemBarcode}
                        value={itemBarcode.find(
                          (item) => item.value === field.value
                        )}
                        onChange={(e) => {
                          if (e) {
                            field.onChange(e.value)
                          }
                        }}
                      />
                    )}
                  />

                  <Country />
                  <Province />
                  <Regency exportPdf={exportPdf} />
                  <Subdistrict />
                  <Village />

                  <Controller
                    control={methods.control}
                    name="address"
                    render={({ field }) => (
                      <Textarea {...field} label="ADDRESS" />
                    )}
                  />
                  <div>
                    <Label>LOGO</Label>
                    <UploadFoto />
                  </div>
                </section>


              </section>
            </FormProvider>
          )}
        </Card.Body>
      </Card>

      <FooterMenu
        export={{ actiontype: "export", onClick: () => exportExcel({ data: methods.getValues() }) }}
        pdf={{ actiontype: "pdf", onClick: () => exportPdf.handleExportPdf() }}
        save={{ actiontype: "save", onClick: methods.handleSubmit(onSubmit), loading: methods.formState.isSubmitting ? "true" : undefined }}
      />
    </section>
  )
}

function Country(): JSX.Element {
  // Hooks
  const { data, isLoading } = useCountry()

  // Form
  const { control, setValue } = useFormContext<DataType>()

  return (
    <Controller
      control={control}
      name="country_id"
      render={({ field, fieldState }) => (
        <Select
          label="COUNTRY"
          placeholder="Select Country"
          error={fieldState.error?.message}
          isLoading={isLoading}
          options={data}
          value={data.find(
            (item) => item.value.toString() === field.value.toString()
          )}
          onChange={(e) => {
            if (e) {
              field.onChange(e.value)
              setValue("province_id", "")
              setValue("regency_id", "")
              setValue("subdistrict_id", "")
              setValue("village_id", "")
            }
          }}
        />
      )}
    />
  )
}

function Province(): JSX.Element {
  // Hooks
  const { data, isLoading, refetch } = useProvince()

  // Form
  const { control, setValue } = useFormContext<DataType>()
  const country_id = useWatch({
    control,
    name: "country_id"
  })

  useEffect(() => {
    refetch(country_id)
    // eslint-disable-next-line
  }, [country_id])

  return (
    <Controller
      control={control}
      name="province_id"
      render={({ field, fieldState }) => (
        <Select
          label="PROVINCE"
          placeholder="Select Province"
          error={fieldState.error?.message}
          isLoading={isLoading}
          options={data}
          value={data.find(
            (item) => item.value.toString() === field.value.toString()
          )}
          onChange={(e) => {
            if (e) {
              field.onChange(e.value)
              setValue("regency_id", "")
              setValue("subdistrict_id", "")
              setValue("village_id", "")
            }
          }}
        />
      )}
    />
  )
}

function Regency({ exportPdf }: { exportPdf?: ReturnType<typeof useExportPdfCompany> }): JSX.Element {
  // Hooks
  const { data, isLoading, refetch } = useRegency()

  // Form
  const { control, setValue, getValues } = useFormContext<DataType>()
  const province_id = useWatch({
    control,
    name: "province_id"
  })
  const regency_id = useWatch({
    control,
    name: "regency_id"
  })

  useEffect(() => {
    refetch(province_id)
    // eslint-disable-next-line
  }, [province_id, regency_id,])

  useEffect(() => {
    const regency_name = data.find((item) => item.value.toString() === regency_id.toString())?.label ?? ""
    setValue("regency_name", regency_name)
    exportPdf?.setData(() => ({ ...getValues(), regency_name }))
  }, [data, regency_id])

  return (
    <Controller
      control={control}
      name="regency_id"
      render={({ field, fieldState }) => (
        <Select
          label="REGENCY"
          placeholder="Select Regency"
          error={fieldState.error?.message}
          isLoading={isLoading}
          options={data}
          value={data.find(
            (item) => item.value.toString() === field.value.toString()
          )}
          onChange={(e) => {
            if (e) {
              field.onChange(e.value)
              setValue("subdistrict_id", "")
              setValue("village_id", "")
            }
          }}
        />
      )}
    />
  )
}

function Subdistrict(): JSX.Element {
  // Hooks
  const { data, isLoading, refetch } = useSubdistrict()

  // Form
  const { control, setValue } = useFormContext<DataType>()
  const regency_id = useWatch({
    control,
    name: "regency_id"
  })

  useEffect(() => {
    refetch(regency_id)
    // eslint-disable-next-line
  }, [regency_id])

  return (
    <Controller
      control={control}
      name="subdistrict_id"
      render={({ field, fieldState }) => (
        <Select
          label="SUBDISCRICT"
          placeholder="Select Subdistrict"
          error={fieldState.error?.message}
          isLoading={isLoading}
          options={data}
          value={data.find(
            (item) => item.value.toString() === field.value.toString()
          )}
          onChange={(e) => {
            if (e) {
              field.onChange(e.value)
              setValue("village_id", "")
            }
          }}
        />
      )}
    />
  )
}

function Village(): JSX.Element {
  // Hooks
  const { data, isLoading, refetch } = useVillage()

  // Form
  const { control } = useFormContext<DataType>()
  const subdistrict_id = useWatch({
    control,
    name: "subdistrict_id"
  })

  useEffect(() => {
    refetch(subdistrict_id)
    // eslint-disable-next-line
  }, [subdistrict_id])

  return (
    <Controller
      control={control}
      name="village_id"
      render={({ field, fieldState }) => (
        <Select
          label="VILLAGE"
          placeholder="Select Village"
          error={fieldState.error?.message}
          isLoading={isLoading}
          options={data}
          value={data.find(
            (item) => item.value.toString() === field.value.toString()
          )}
          onChange={(e) => {
            if (e) {
              field.onChange(e.value)
            }
          }}
        />
      )}
    />
  )
}


function UploadFoto() {
  const api = useApi()
  const { REACT_APP_BASE_URL_API } = process.env

  const { isActive, toggle } = useToggle(false)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)


  const { control, setValue } = useFormContext<DataType>()

  const logoPath = useWatch({
    control,
    name: "logo_path"
  })


  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (!selected) return

    setFile(selected)
    setPreview(URL.createObjectURL(selected))
  }

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview)
    }
  }, [preview])

  const handleReset = () => {
    setFile(null)
    setPreview(null)
  }

  const handleSave = () => {
    const formData = new FormData()
    formData.append("fileupload", file!)

    toast.promise(
      api.post("/settingcompany/upload-logo", formData),
      {
        loading: "Loading...",
        success: (res) => res.data.message,
        error: (err) => err.response.data.message
      }
    ).then((res) => {
      setValue("logo_path", res.data.payload.namafile)
      toggle()
    })
      .catch(() => { })
      .finally()
  }


  return (
    <>
      <Button
        color="primary"
        className="mb-2"
        style={{ width: 200 }}
        onClick={toggle}
      >
        ADD LOGO
      </Button>

      <div>
        <img
          src={
            logoPath
              ? `${REACT_APP_BASE_URL_API}/getimage/?imgpath=${logoPath}`
              : "https://t4.ftcdn.net/jpg/05/17/53/57/360_F_517535712_q7f9QC9X6TQxWi6xYZZbMmw5cnLMr279.jpg"
          }
          alt="Logo item"
          width={200}
          height={200}
          className="object-contain aspect-square"
        />

      </div>


      {isActive && (
        <Modal isOpen title="UPLOAD FOTO" closeModal={toggle}>
          <Modal.Body>
            <div className="flex min-h-[320px] items-center justify-center">
              <div className="w-full max-w-xl">

                {!preview ? (
                  <label
                    htmlFor="upload-file"
                    className="
                  flex h-64 cursor-pointer flex-col items-center
                  justify-center rounded-xl border-2 border-dashed
                  border-gray-300 bg-gray-50 text-center
                  transition hover:border-primary hover:bg-primary/5
                "
                  >
                    <div className="
                          mb-4 flex h-14 w-14 items-center justify-center
                          rounded-full bg-primary/10 text-primary
                          group-hover:scale-105 transition
                        ">
                      <svg
                        className="h-7 w-7"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 17h3a3 3 0 0 0 0-6h-.025a5.56 5.56 0 0 0 .025-.5A5.5 5.5 0 0 0 7.207 9.021C7.137 9.017 7.071 9 7 9a4 4 0 1 0 0 8h2.167M12 19v-9m0 0-2 2m2-2 2 2"
                        />
                      </svg>
                    </div>
                    <p className="text-sm font-semibold">
                      Click to upload
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      JPG / PNG • Max 2MB
                    </p>

                    <input
                      id="upload-file"
                      type="file"
                      accept="image/jpeg, image/png"
                      className="hidden"
                      onChange={handleChange}
                    />
                  </label>
                ) : (
                  <div className="relative">
                    <img
                      src={preview}
                      alt="Preview"
                      className="h-80 w-full rounded-xl object-contain bg-gray-100"
                    />


                    <div className="absolute bottom-3 right-3 flex gap-2">
                      <button
                        type="button"
                        onClick={handleReset}
                        className="rounded-full  px-3 py-1 text-xs shadow bg-red-500 text-white"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button
              color="primary"
              disabled={!file}
              onClick={handleSave}
            >
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  )

}

export const dropdownAverage: SelectOptionType[] = [
  { value: "1", label: "Average Price" },
  { value: "2", label: "Last Price" }
  // {value: '3', label: 'Standar Price'},
]

export const itemBarcode: SelectOptionType[] = [
  { value: "1", label: "Item barcode" },
  { value: "2", label: "sku" }
]

export const periodeAdjustment: SelectOptionType[] = [
  { value: "1", label: "Day" },
  { value: "2", label: "Week" },
  { value: "3", label: "Month" },
  { value: "4", label: "Quarter" },
  { value: "5", label: "Year" },
]